import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskGroupInterface } from '../../../interfaces/TaskInterface';
import Dropdown from '../../dropdown/mainDropdown';
import DropdownItem from '../../dropdown/dropdownItem';
import { ProjectModel } from '../../../mobx/models/project';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import IconButton from '../../button/iconButton';
import { CHIP_SIZE, CHIP_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../global/constants';
import Chip from '../../chipComponent';

// Interface for GroupDropdown component props
interface Props {
  onUpdateGroup?: (group: TaskGroupInterface) => void;
  isCreateTaskModal?: boolean;
  currentProject?: ProjectModel | ProjectTemplateModel;
  value?: string;
  isIconButton?: boolean;
}

const StatusDropdown: React.FC<Props> = (props) => {
  const { onUpdateGroup, isCreateTaskModal, value, currentProject, isIconButton } = props;
  // State variables
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  //Mobx store variables
  const { taskStore } = rootStore;
  const { createTaskInput } = taskStore;

  // Find the currently selected group from the project's task groups
  const selectedGroup = currentProject?.taskGroups.find((group) => group.id === value);

  /**
   * Effect to set default group when creating a new task
   * Sets the first group as default if no group is selected and groups exist
   */
  useEffect(() => {
    if (isCreateTaskModal && !selectedGroup && currentProject?.taskGroups && currentProject?.taskGroups?.length > 0) {
      createTaskInput?.setGroupId(currentProject?.taskGroups[0].id);
    }
  }, [isCreateTaskModal, selectedGroup, currentProject, createTaskInput]);

  /**
   * Handles the selection of a task group
   * @param {TaskGroupInterface} taskGroup - The selected task group
   */
  const onClickGroup = useCallback(
    (taskGroup: TaskGroupInterface) => {
      if (isCreateTaskModal) {
        createTaskInput.setGroupId(taskGroup.id);
      } else if (onUpdateGroup) {
        onUpdateGroup(taskGroup);
        return;
      }
    },
    [createTaskInput, isCreateTaskModal, onUpdateGroup]
  );

  // Renders the list of available task groups
  const renderGroupList = useMemo(() => {
    return currentProject?.taskGroups.map((group: TaskGroupInterface) => (
      <DropdownItem
        label={group.name}
        key={group?.id}
        onClick={() => onClickGroup(group)}
        isSelected={selectedGroup?.id === group.id}
      />
    ));
  }, [currentProject?.taskGroups, onClickGroup, selectedGroup?.id]);

  return (
    <Dropdown
      content={
        isIconButton ? (
          <IconButton
            iconName='updated-group-icon'
            variant={ICON_BUTTON_TYPE.TERTIARY}
            size={ICON_BUTTON_SIZE.MEDIUM}
            className={isDropdownOpen ? 'active-dropdown' : ''}
          />
        ) : (
          <Chip
            size={isCreateTaskModal ? CHIP_SIZE.SMALL : CHIP_SIZE.MEDIUM}
            title={selectedGroup?.name || 'Group'}
            leadingIconName={isCreateTaskModal ? 'updated-group-icon' : ''}
            variant={isCreateTaskModal ? CHIP_TYPE.FILL : CHIP_TYPE.EMPTY}
            isActive={isDropdownOpen}
          />
        )
      }
      trigger='click'
      modalTitle='Group'
      updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
      {renderGroupList}
    </Dropdown>
  );
};
export default observer(StatusDropdown);
